import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createHospitalAdmission,
  lookupHospitalEmployeeByMatricule,
  type CreateHospitalAdmissionPayload,
} from "../api/hospitalApi";
import { getAuthToken } from "../auth";
import { searchAdmissionPatients } from "../data";
import type { AdmissionPatientCandidate } from "../types";

export type AdmissionCreateCandidate = AdmissionPatientCandidate & {
  companyId: number;
  employeeId: number;
  employeeMatricule: string;
  patientNom: string;
  patientPrenom: string;
  patientType: "Employé" | "Ayant droit";
  patientId?: number | null;
};

function splitName(fullName: string): { prenom: string; nom: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { prenom: "—", nom: "—" };
  if (parts.length === 1) return { prenom: parts[0], nom: parts[0] };
  return { prenom: parts[0], nom: parts.slice(1).join(" ") };
}

function mapLookupToCandidates(
  lookup: Awaited<ReturnType<typeof lookupHospitalEmployeeByMatricule>>,
): AdmissionCreateCandidate[] {
  const employee = lookup.employee;
  const { prenom, nom } = splitName(employee.employee_name);
  const results: AdmissionCreateCandidate[] = [
    {
      id: `emp-${employee.id}`,
      name: employee.employee_name,
      systemId: employee.matricule,
      type: "employee",
      company: employee.company_name,
      subtitle: employee.company_name,
      companyId: employee.company_id,
      employeeId: employee.id,
      employeeMatricule: employee.matricule,
      patientNom: nom,
      patientPrenom: prenom,
      patientType: "Employé",
      patientId: null,
    },
  ];

  for (const member of lookup.family_members) {
    const split = splitName(member.name);
    results.push({
      id: `dep-${member.id}`,
      name: member.name,
      systemId: employee.matricule,
      type: "beneficiary",
      company: employee.company_name,
      subtitle: member.relationship || "Ayant droit",
      companyId: employee.company_id,
      employeeId: employee.id,
      employeeMatricule: employee.matricule,
      patientNom: split.nom,
      patientPrenom: split.prenom,
      patientType: "Ayant droit",
      patientId: member.id,
    });
  }

  return results;
}

export function useCreateHospitalAdmission() {
  const navigate = useNavigate();
  const [systemId, setSystemId] = useState("");
  const [results, setResults] = useState<AdmissionCreateCandidate[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [dateAdmission, setDateAdmission] = useState(() => new Date().toISOString().slice(0, 10));
  const [motif, setMotif] = useState("");
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const selected = useMemo(
    () => results.find((item) => item.id === selectedPatientId) ?? null,
    [results, selectedPatientId],
  );

  const search = useCallback(async () => {
    const query = systemId.trim();
    setHasSearched(true);
    setSelectedPatientId(null);
    setError(null);

    if (!query) {
      setResults([]);
      return;
    }

    if (!getAuthToken()) {
      setUsingApi(false);
      const mock = searchAdmissionPatients(query).map((item) => ({
        ...item,
        companyId: 0,
        employeeId: 0,
        employeeMatricule: item.systemId,
        patientNom: item.name,
        patientPrenom: item.name,
        patientType: (item.type === "employee" ? "Employé" : "Ayant droit") as
          | "Employé"
          | "Ayant droit",
        patientId: null,
      }));
      setResults(mock);
      return;
    }

    setSearching(true);
    setUsingApi(true);
    try {
      const lookup = await lookupHospitalEmployeeByMatricule(query);
      setResults(mapLookupToCandidates(lookup));
    } catch (err) {
      setResults([]);
      setError(err instanceof Error ? err.message : "Recherche impossible.");
    } finally {
      setSearching(false);
    }
  }, [systemId]);

  const submit = useCallback(async () => {
    if (!selected) {
      setError("Sélectionnez un patient.");
      return false;
    }
    if (!dateAdmission) {
      setError("La date d'admission est obligatoire.");
      return false;
    }

    if (!getAuthToken()) {
      setError(null);
      return true;
    }

    const payload: CreateHospitalAdmissionPayload = {
      company_id: selected.companyId,
      employee_id: selected.employeeId,
      employee_matricule: selected.employeeMatricule,
      patient_nom: selected.patientNom,
      patient_prenom: selected.patientPrenom,
      patient_type: selected.patientType,
      date_admission: dateAdmission,
      motif_admission: motif.trim(),
      patient_id: selected.patientId ?? null,
    };

    setSubmitting(true);
    setError(null);
    try {
      const admission = await createHospitalAdmission(payload);
      navigate(`/dashboard/admissions/${admission.id}`);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Création impossible.");
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [selected, dateAdmission, motif, navigate]);

  return {
    systemId,
    setSystemId,
    results,
    hasSearched,
    selectedPatientId,
    setSelectedPatientId,
    selected,
    dateAdmission,
    setDateAdmission,
    motif,
    setMotif,
    searching,
    submitting,
    error,
    usingApi,
    search,
    submit,
  };
}
